<template>
  <v-dialog v-model="value" max-width="900px" persistent scrollable>
    <v-card class="pa-4">
      <v-card-title class="py-4 px-6 d-flex align-center">
        <v-icon color="blue-grey darken-4" class="mr-3">mdi-bell-ring-outline</v-icon>
        <span class="text-h6 font-bold text-blue-grey-darken-4"
          >Manual Notification Dispatcher</span
        >
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-6 mt-4 bg-gray-50">
        <div class="p-2 bg-gray-50">
          <v-row>
            <!-- Step 1: Usecase & Channel -->
            <v-col cols="12" md="6">
              <label class="text-caption font-bold text-gray-600 uppercase mb-1 d-block"
                >1. Target Module (Usecase)</label
              >
              <v-select
                v-model="form.useCase"
                :items="useCases"
                item-text="name"
                item-value="id"
                label="Select Usecase"
                outlined
                dense
                hide-details
                class="rounded-lg bg-white"
                @change="onUseCaseChange"
                prepend-inner-icon="mdi-briefcase-outline"
              ></v-select>
            </v-col>

            <v-col cols="12" md="6">
              <label class="text-caption font-bold text-gray-600 uppercase mb-1 d-block"
                >2. Communication Channel</label
              >
              <v-select
                v-model="form.channel"
                :items="channels"
                label="Select Channel"
                outlined
                dense
                hide-details
                class="rounded-lg bg-white"
                @change="onChannelChange"
                prepend-inner-icon="mdi-message-text-outline"
              ></v-select>
            </v-col>

            <v-col cols="12" class="pt-0 pb-2" v-if="selectedChannelQuotaReached">
              <v-alert type="error" outlined dense class="rounded-lg mb-0 text-caption">
                <div class="font-bold">Monthly limit reached for {{ form.channel }}!</div>
                <div>
                  Used: <strong>{{ quotaUsage[form.channel].used }}</strong> / Limit:
                  <strong>{{ quotaUsage[form.channel].limit }}</strong
                  >. You will not be able to dispatch new notifications on this channel.
                </div>
              </v-alert>
            </v-col>

            <!-- Step 2: Template Selection -->
            <v-col cols="12">
              <div class="d-flex align-center justify-space-between mb-1">
                <label class="text-caption font-bold text-gray-600 uppercase"
                  >3. Message Template</label
                >
                <v-btn
                  v-if="selectedTemplate"
                  x-small
                  text
                  color="blue darken-4"
                  @click="showPreview = !showPreview"
                  class="font-bold px-2 rounded-lg"
                >
                  <v-icon left x-small>{{
                    showPreview ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
                  }}</v-icon>
                  {{ showPreview ? 'Hide Preview' : 'Show Preview' }}
                </v-btn>
              </div>
              <v-select
                v-model="form.templateId"
                :items="filteredTemplates"
                item-text="name"
                item-value="_id"
                label="Choose Template"
                outlined
                dense
                hide-details
                class="rounded-lg bg-white"
                :disabled="!form.channel"
                prepend-inner-icon="mdi-file-document-outline"
              >
                <template v-slot:item="{ item }">
                  <div class="d-flex align-center py-2">
                    <v-icon small class="mr-2">{{ getChannelIcon(item.type) }}</v-icon>
                    <div>
                      <div class="font-bold">{{ item.name }}</div>
                      <div class="text-caption grey--text">{{ item.templateCode }}</div>
                    </div>
                  </div>
                </template>
              </v-select>

              <!-- Inline Template Preview -->
              <v-expand-transition>
                <div v-if="showPreview && selectedTemplate" class="mt-4">
                  <v-card
                    outlined
                    class="rounded-xl pa-4 bg-white border-2 border-dashed border-blue-100"
                  >
                    <div class="d-flex align-center mb-3">
                      <v-chip
                        x-small
                        :color="getChannelColor(selectedTemplate.type)"
                        class="white--text font-bold mr-2"
                      >
                        {{ selectedTemplate.type }}
                      </v-chip>
                      <span class="text-caption font-bold text-gray-700">{{
                        selectedTemplate.name
                      }}</span>
                    </div>
                    <div
                      class="preview-content-box rounded-lg pa-3 bg-gray-50 text-caption"
                      v-html="selectedTemplate.content || selectedTemplate.letterContent"
                    ></div>
                  </v-card>
                </div>
              </v-expand-transition>
            </v-col>

            <!-- Step 3: Customer Search -->
            <v-col cols="12">
              <label class="text-caption font-bold text-gray-600 uppercase mb-1 d-block"
                >4. Recipients Selection</label
              >
              <div class="d-flex mb-4 gap-2" v-if="form.useCase !== 'other'">
                <v-btn
                  :input-value="dispatchMode === 'manual'"
                  @click="dispatchMode = 'manual'"
                  outlined
                  :color="dispatchMode === 'manual' ? 'blue darken-4' : 'grey'"
                  class="rounded-lg flex-grow-1"
                >
                  <v-icon left>mdi-account-search-outline</v-icon>
                  Search & Add
                </v-btn>
                <v-btn
                  :input-value="dispatchMode === 'excel'"
                  @click="dispatchMode = 'excel'"
                  outlined
                  :color="dispatchMode === 'excel' ? 'blue darken-4' : 'grey'"
                  class="rounded-lg flex-grow-1"
                >
                  <v-icon left>mdi-file-excel-outline</v-icon>
                  Excel Bulk
                </v-btn>
              </div>

              <template v-if="dispatchMode === 'manual'">
                <v-autocomplete
                  v-model="selectedCustomer"
                  :items="customerSearchResults"
                  :loading="searchingCustomers"
                  :search-input.sync="customerSearchQuery"
                  item-text="name"
                  item-value="_id"
                  label="Search by Name, CRN, Mobile or Email"
                  outlined
                  dense
                  hide-details
                  class="rounded-lg bg-white"
                  hide-no-data
                  return-object
                  placeholder="Start typing to search..."
                  @change="addCustomer"
                  :disabled="!form.useCase"
                  prepend-inner-icon="mdi-account-search-outline"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title class="font-bold d-flex align-center">
                        {{ item.name }}
                        <v-chip
                          x-small
                          class="ml-2"
                          color="blue lighten-4"
                          text-color="blue darken-4"
                        >
                          {{ item.cases.length }} Cases
                        </v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption mb-1">
                        {{ item.mobile }} | {{ item.email }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle
                        v-for="c in item.cases"
                        :key="c._id"
                        class="text-caption grey--text text--darken-2"
                      >
                        <v-icon x-small>mdi-file-document-outline</v-icon>
                        {{ c.caseNo }} ({{ getCaseStatus(c) }})
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </template>

              <template v-else>
                <v-card outlined class="rounded-xl pa-4 bg-white border-2 border-dashed">
                  <div class="d-flex align-center justify-space-between mb-4">
                    <div class="text-caption font-bold text-gray-700">
                      <v-icon color="success" class="mr-1">mdi-information-outline</v-icon>
                      {{
                        form.useCase === 'other'
                          ? form.channel === 'EMAIL'
                            ? "Upload Excel with 'Name' and 'Email'"
                            : "Upload Excel with 'Name' and 'Number'"
                          : "Upload Excel with 'Customer CRN' and 'Case No'"
                      }}
                    </div>
                    <v-btn
                      small
                      text
                      color="blue darken-2"
                      class="font-bold"
                      @click="downloadSample"
                    >
                      <v-icon left small>mdi-download-outline</v-icon>
                      Sample Excel
                    </v-btn>
                  </div>
                  <v-file-input
                    v-model="excelFile"
                    label="Choose Excel File"
                    outlined
                    dense
                    hide-details
                    accept=".xlsx, .xls"
                    prepend-icon=""
                    prepend-inner-icon="mdi-file-upload-outline"
                    class="rounded-lg"
                    @change="onExcelFileChange"
                    :loading="processingExcel"
                  ></v-file-input>
                </v-card>
              </template>
            </v-col>

            <!-- Selected Customers List -->
            <v-col cols="12" v-if="form.recipients.length > 0">
              <div class="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div
                  class="bg-blue-grey lighten-5 px-6 py-3 d-flex align-center justify-space-between border-b"
                >
                  <span class="text-subtitle-2 font-bold blue-grey--text text--darken-4">
                    <v-icon left small>mdi-account-group-outline</v-icon>
                    Selected Recipients ({{ selectedCount }})
                  </span>
                  <v-btn
                    x-small
                    text
                    color="error"
                    class="font-bold rounded-lg"
                    @click="form.recipients = []"
                  >
                    Clear All
                  </v-btn>
                </div>

                <div class="recipient-list max-h-[400px] overflow-y-auto">
                  <div
                    v-for="(recipient, rIdx) in form.recipients"
                    :key="recipient._id"
                    class="customer-block border-b last:border-0 hover:bg-gray-50 transition-colors"
                  >
                    <!-- Customer Row -->
                    <div class="d-flex align-center pa-4">
                      <v-checkbox
                        v-model="recipient.selected"
                        @change="toggleCustomerCases(recipient)"
                        hide-details
                        dense
                        color="success"
                        class="mt-0 mr-4"
                      ></v-checkbox>

                      <div class="flex-grow-1">
                        <div class="d-flex align-center flex-wrap gap-2 mb-1">
                          <v-icon
                            v-if="!isRecipientValid(recipient)"
                            color="red"
                            class="mr-1 pulse-animation"
                            small
                            >mdi-alert</v-icon
                          >
                          <span
                            :class="[
                              'text-body-2 font-bold',
                              !isRecipientValid(recipient) ? 'red--text' : ''
                            ]"
                            >{{ recipient.name }}</span
                          >
                          <v-chip x-small label class="font-bold bg-gray-100">{{
                            recipient.crn
                          }}</v-chip>

                          <!-- One row info if single case -->
                          <template v-if="recipient.cases.length === 1">
                            <v-chip x-small outlined color="blue-grey lighten-1" class="ml-2"
                              >#{{ recipient.cases[0].caseNo }}</v-chip
                            >
                            <v-chip
                              x-small
                              :color="getStatusColor(getCaseStatus(recipient.cases[0]))"
                              class="white--text font-bold"
                              >{{ getCaseStatus(recipient.cases[0]) }}</v-chip
                            >
                          </template>

                          <span class="text-caption grey--text text--darken-1 ml-auto">
                            <v-icon x-small class="mr-1">mdi-email-outline</v-icon
                            >{{ recipient.email }}
                          </span>
                          <span class="text-caption grey--text text--darken-1 ml-4">
                            <v-icon x-small class="mr-1">mdi-phone-outline</v-icon
                            >{{ recipient.mobile }}
                          </span>
                        </div>
                      </div>

                      <v-btn icon x-small color="error" @click="removeCustomer(rIdx)" class="ml-4">
                        <v-icon small>mdi-close-circle-outline</v-icon>
                      </v-btn>
                    </div>

                    <!-- Multiple Cases (Branches) -->
                    <div
                      v-if="recipient.cases.length > 1"
                      class="ml-12 pb-4 space-y-1 border-l-2 border-dashed border-gray-200 pl-4"
                    >
                      <div
                        v-for="c in recipient.cases"
                        :key="c._id"
                        class="case-item d-flex align-center py-1"
                      >
                        <v-checkbox
                          v-model="c.selected"
                          @change="updateCustomerSelection(recipient)"
                          hide-details
                          dense
                          color="success"
                          class="mt-0 mr-3"
                        ></v-checkbox>
                        <div class="d-flex align-center flex-grow-1">
                          <span class="text-caption font-bold mr-4">Case #{{ c.caseNo }}</span>
                          <v-chip
                            x-small
                            :color="getStatusColor(getCaseStatus(c))"
                            class="white--text font-bold px-2"
                            >{{ getCaseStatus(c) }}</v-chip
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-6 bg-white">
        <v-spacer></v-spacer>
        <v-btn text @click="close" class="px-6 normal-case">Cancel</v-btn>
        <v-btn
          color="blue darken-4"
          class="px-8 white--text font-bold normal-case rounded-lg"
          elevation="0"
          :disabled="!isFormValid"
          :loading="sending"
          @click="sendNotification"
        >
          <v-icon left>mdi-send-outline</v-icon>
          Send to {{ selectedCount }} Recipients
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import * as XLSX from 'xlsx'

export default {
  name: 'SendNotificationDialog',
  props: {
    value: Boolean
  },
  created() {
    this.debouncedSearchCustomers = _.debounce(this.searchCustomers, 500)
  },
  data() {
    return {
      sending: false,
      showPreview: false,
      loadingUseCases: false,
      searchingCustomers: false,
      useCases: [
        { id: 'loan_management', name: 'Loan Management' },
        { id: 'collection_management', name: 'Collection Management' },
        { id: 'other', name: 'Other' }
      ],
      dispatchMode: 'manual', // 'manual' or 'excel'
      excelFile: null,
      templates: [],
      channels: ['EMAIL', 'WHATSAPP', 'SMS'],
      customerSearchQuery: '',
      customerSearchResults: [],
      selectedCustomer: null,
      form: {
        useCase: '',
        channel: '',
        templateId: '',
        recipients: []
      },
      processingExcel: false,
      quotaUsage: null,
      loadingQuota: false
    }
  },
  watch: {
    value(val) {
      if (val) {
        // this.fetchUseCases()
        this.fetchTemplates()
        this.fetchQuotaUsage()
      } else {
        this.resetForm()
      }
    },
    customerSearchQuery(val) {
      if (val && val.length > 2) {
        this.debouncedSearchCustomers(val)
      }
    }
  },
  computed: {
    selectedChannelQuotaReached() {
      if (!this.form.channel || !this.quotaUsage) return false
      const usage = this.quotaUsage[this.form.channel]
      return !!(usage && usage.reached)
    },
    filteredTemplates() {
      if (!this.form.channel || !this.form.useCase) return []
      return this.templates.filter((t) => {
        if (t.type !== this.form.channel || t.type === 'ATTACHMENT') return false
        if (t.module !== this.form.useCase) return false
        if (t.type === 'WHATSAPP') {
          return t.whatsappStatus === 'APPROVED'
        }
        if (t.type === 'SMS') {
          return t.smsStatus === 'APPROVED'
        }
        return true
      })
    },
    selectedTemplate() {
      return this.templates.find((t) => t._id === this.form.templateId)
    },
    selectedCount() {
      return this.form.recipients.reduce(
        (acc, r) => acc + (r.cases ? r.cases.filter((c) => c.selected).length : 0),
        0
      )
    },
    isFormValid() {
      return (
        this.form.useCase &&
        this.form.channel &&
        this.form.templateId &&
        this.selectedCount > 0 &&
        !this.selectedChannelQuotaReached
      )
    }
  },
  methods: {
    // fetchSubCases removed as we use static useCases for now
    async fetchTemplates() {
      try {
        const response = await this.$axios.get('/letter-templates?limit=*')
        this.templates = response.data?.data || []
      } catch (error) {
        console.error('Error fetching templates:', error)
      }
    },
    async fetchQuotaUsage() {
      try {
        this.loadingQuota = true
        const response = await this.$axios.get('/organization/communication-usage')
        if (response.data && response.data.success) {
          this.quotaUsage = response.data.usage
        }
      } catch (error) {
        console.error('Error fetching quota usage:', error)
      } finally {
        this.loadingQuota = false
      }
    },
    async searchCustomers(query) {
      if (!this.form.useCase) return
      try {
        this.searchingCustomers = true
        const response = await this.$axios.get('/customers/search-linked', {
          params: {
            useCase: this.form.useCase,
            search: query
          }
        })
        this.customerSearchResults = response.data?.data || []
      } catch (error) {
        console.error('Error searching customers:', error)
      } finally {
        this.searchingCustomers = false
      }
    },
    addCustomer(customer) {
      if (customer && !this.form.recipients.some((r) => r._id === customer._id)) {
        // Initialize selection state
        const newRecipient = {
          ...customer,
          selected: true,
          cases: (customer.cases || []).map((c) => ({ ...c, selected: true }))
        }
        this.form.recipients.push(newRecipient)
      }
      this.$nextTick(() => {
        this.selectedCustomer = null
      })
    },
    toggleCustomerCases(recipient) {
      recipient.cases.forEach((c) => {
        c.selected = recipient.selected
      })
    },
    updateCustomerSelection(recipient) {
      recipient.selected = recipient.cases.some((c) => c.selected)
    },
    getCaseStatus(c) {
      if (c.loanDetails && c.loanDetails.currentStatus) {
        return c.loanDetails.currentStatus
      }
      return c.status
    },
    getStatusColor(status) {
      const colors = {
        OPEN: 'blue darken-2',
        IN_PROGRESS: 'amber darken-2',
        CLOSED: 'green darken-2',
        RESOLVED: 'green darken-2',
        REJECTED: 'red darken-2',
        DISBURSED: 'green darken-2',
        APPROVED: 'blue darken-2',
        PENDING: 'amber darken-2'
      }
      return colors[status?.toUpperCase()] || 'grey darken-1'
    },
    removeCustomer(index) {
      this.form.recipients.splice(index, 1)
    },
    onUseCaseChange() {
      this.form.recipients = []
      this.customerSearchResults = []
      this.showPreview = false
      this.excelFile = null
      if (this.form.useCase === 'other') {
        this.dispatchMode = 'excel'
      } else {
        this.dispatchMode = 'manual'
      }
    },
    async onExcelFileChange(file) {
      if (!file) return

      try {
        this.processingExcel = true
        const reader = new FileReader()
        reader.onload = async (e) => {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          const sheetName = workbook.SheetNames[0]
          const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName])

          if (rows.length === 0) {
            this.$toast.error('Excel file is empty')
            return
          }

          if (this.form.useCase === 'other') {
            const foundRecipients = []
            rows.forEach((row, idx) => {
              const name = row['Name'] || row['name'] || `Recipient ${idx + 1}`
              const email = row['Email'] || row['email'] || ''
              const mobile = row['Number'] || row['number'] || row['Mobile'] || row['mobile'] || ''

              const tempId = `temp_${Date.now()}_${idx}`
              foundRecipients.push({
                _id: tempId,
                name,
                crn: 'N/A',
                mobile,
                email,
                selected: true,
                cases: [
                  {
                    _id: `case_${tempId}`,
                    caseNo: 'N/A',
                    status: 'OTHER',
                    selected: true
                  }
                ]
              })
            })

            foundRecipients.forEach((r) => {
              if (
                !this.form.recipients.some(
                  (existing) =>
                    (r.email && existing.email === r.email) ||
                    (r.mobile && existing.mobile === r.mobile)
                )
              ) {
                this.form.recipients.push(r)
              }
            })
            this.$toast.success(`Added ${foundRecipients.length} recipients from Excel`)
            this.excelFile = null
            return
          }

          // Fetch customers from backend based on Case Nos in Excel
          const response = await this.$axios.post('/notifications/manual-bulk-excel', { rows })
          const foundRecipients = response.data?.data?.data || []

          if (foundRecipients.length === 0) {
            this.$toast.warning('No matching customers found for the provided Case Nos')
          } else {
            foundRecipients.forEach((customer) => {
              this.addCustomer(customer)
            })
            this.$toast.success(`Added ${foundRecipients.length} customers from Excel`)
          }
          this.excelFile = null // Reset after processing
        }
        reader.readAsArrayBuffer(file)
      } catch (error) {
        console.error('Error processing Excel:', error)
        this.$toast.error('Failed to process Excel file')
      } finally {
        this.processingExcel = false
      }
    },
    onChannelChange() {
      this.form.templateId = ''
      // Re-validate all selected recipients when channel changes
      this.form.recipients.forEach((r) => {
        if (!this.isRecipientValid(r)) {
          r.selected = false
          if (r.cases) {
            r.cases.forEach((c) => (c.selected = false))
          }
        }
      })
    },
    isRecipientValid(recipient) {
      if (!this.form.channel) return true
      if (this.form.channel === 'EMAIL') {
        return !!recipient.email
      }
      if (this.form.channel === 'WHATSAPP' || this.form.channel === 'SMS') {
        return !!recipient.mobile
      }
      return true
    },
    getChannelIcon(type) {
      const icons = {
        EMAIL: 'mdi-email-outline',
        WHATSAPP: 'mdi-whatsapp',
        SMS: 'mdi-message-text-outline'
      }
      return icons[type] || 'mdi-bell-outline'
    },
    getChannelColor(type) {
      const colors = {
        EMAIL: 'blue',
        WHATSAPP: 'green',
        SMS: 'orange'
      }
      return colors[type] || 'grey'
    },
    async sendNotification() {
      try {
        this.sending = true

        // Filter selected recipients and cases
        const selectedRecipients = this.form.recipients
          .filter((r) => r.cases.some((c) => c.selected))
          .map((r) => ({
            ...r,
            cases: r.cases.filter((c) => c.selected)
          }))

        if (selectedRecipients.length === 0) {
          this.$toast.error('Please select at least one recipient/case')
          return
        }

        const payload = {
          useCase: this.form.useCase,
          channel: this.form.channel,
          templateId: this.form.templateId
        }

        if (this.form.useCase === 'other') {
          payload.recipients = selectedRecipients.map((r) => ({
            name: r.name,
            email: r.email,
            mobile: r.mobile
          }))
        } else {
          payload.recipientIds = selectedRecipients.map((r) => r._id)
          payload.caseIds = selectedRecipients.flatMap((r) => r.cases.map((c) => c._id))
        }

        await this.$axios.post('/notifications/manual-bulk', payload)

        this.$toast.success(`Successfully sent notifications to ${this.selectedCount} recipients`)
        this.close()
      } catch (error) {
        console.error('Error sending notifications:', error)
        this.$toast.error(error.response?.data?.message || 'Failed to send notifications')
      } finally {
        this.sending = false
      }
    },
    async downloadSample() {
      try {
        let data = []
        let filename = 'bulk_notification_sample.xlsx'
        if (this.form.useCase === 'other') {
          if (this.form.channel === 'EMAIL') {
            data = [
              {
                Name: 'John Doe',
                Email: 'john@example.com'
              }
            ]
            filename = 'bulk_email_other_sample.xlsx'
          } else {
            data = [
              {
                Name: 'John Doe',
                Number: '919876543210'
              }
            ]
            filename = 'bulk_mobile_other_sample.xlsx'
          }
        } else {
          data = [
            {
              'Customer CRN': 'CRN001',
              'Case No': 'CASE123'
            }
          ]
          filename = 'bulk_notification_sample.xlsx'
        }
        const worksheet = XLSX.utils.json_to_sheet(data)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sample')
        XLSX.writeFile(workbook, filename)
      } catch (error) {
        console.error('Error generating sample:', error)
        this.$toast.error('Failed to generate sample file')
      }
    },
    close() {
      this.$emit('input', false)
    },
    resetForm() {
      this.form = {
        useCase: '',
        channel: '',
        templateId: '',
        recipients: []
      }
      this.customerSearchResults = []
      this.customerSearchQuery = ''
    }
  }
}
</script>

<style scoped>
.preview-content-box {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
}
.gap-2 {
  gap: 8px;
}
.pulse-animation {
  animation: pulse-red 2s infinite;
}
@keyframes pulse-red {
  0% {
    transform: scale(0.95);
    opacity: 0.7;
  }
  70% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.7;
  }
}
</style>
